import { NavLink, useLocation } from 'react-router-dom';
import {
  Users,
  LayoutDashboard,
  ShieldCheck,
  Euro,
  GraduationCap,
  LogOut,
  Loader2,
  User,
  AlertCircle,
  MessageSquare,
  FolderOpen,
  ClipboardList,
  PenTool,
  Settings,
} from 'lucide-react';
import { useMe, useLogout } from '../lib/useMe.js';
import { getNavTargetForApprenants } from '../lib/useLastApprenantId.js';

/**
 * Sidebar V2 — navigation uniquement.
 * Le bouton "Nouvel apprenant" est désormais dans le header de la page Apprenants
 * (cf. ApprenantDetailPage.tsx) — pas dans la sidebar, pour suivre la convention V1
 * et garder la sidebar focalisée sur la navigation.
 * Footer : statut auth (email connecté) + bouton Déconnexion.
 */
export function Sidebar() {
  const { user, isAuthed, isLoading } = useMe();
  const { logout, isPending: logoutPending } = useLogout();

  return (
    <aside className="fixed inset-y-0 left-0 z-40 mt-8 flex w-60 flex-col border-r border-neutral-200 bg-white">
      <div className="border-b border-neutral-200 px-5 py-5">
        <div className="text-primary-900 text-base font-bold">Andalous COCKPIT</div>
        <div className="text-primary-500 text-xs font-semibold uppercase tracking-wider">
          V2 · Refonte
        </div>
      </div>

      <nav className="mt-3 flex-1 space-y-1 px-2 py-3 text-sm">
        <NavItem
          to="/dashboard"
          icon={<LayoutDashboard className="h-4 w-4" />}
          label="Tableau de bord"
          disabled
        />
        <NavItem
          to={getNavTargetForApprenants()}
          icon={<Users className="h-4 w-4" />}
          label="Apprenants"
          matchPath="/apprenants"
        />
        <NavItem
          to="/qualiopi"
          icon={<ShieldCheck className="h-4 w-4" />}
          label="Qualiopi"
          disabled
        />
        <NavItem to="/finance" icon={<Euro className="h-4 w-4" />} label="Finance" disabled />
        <NavItem
          to="/pedagogie"
          icon={<GraduationCap className="h-4 w-4" />}
          label="Pédagogie"
          disabled
        />
        <NavItem
          to="/communications"
          icon={<MessageSquare className="h-4 w-4" />}
          label="Communications"
        />
        <NavItem to="/documents" icon={<FolderOpen className="h-4 w-4" />} label="Documents" />
        <NavItem to="/forms" icon={<ClipboardList className="h-4 w-4" />} label="Formulaires" />
        <NavItem
          to="/emargements"
          icon={<PenTool className="h-4 w-4" />}
          label="Émargements"
          matchPath="/emargements"
        />
        <NavItem
          to="/settings/formateurs"
          icon={<Settings className="h-4 w-4" />}
          label="Paramètres"
          matchPath="/settings"
        />
        {/* Sous-menu Paramètres (visible quand /settings/*) */}
        <NestedNavItem to="/settings/formateurs" label="Formateurs" parentPath="/settings" />
        <NestedNavItem
          to="/settings/contrats-formateurs"
          label="Contrats formateurs"
          parentPath="/settings"
        />
      </nav>

      {/* Footer : statut auth */}
      <div className="border-t border-neutral-200 px-3 py-3">
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-neutral-500">
            <Loader2 className="h-3 w-3 animate-spin" />
            Vérification auth…
          </div>
        ) : isAuthed && user ? (
          <>
            <div className="mb-2 flex items-center gap-2">
              <div className="bg-primary-100 text-primary-700 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold">
                <User className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-semibold text-neutral-900">
                  {user.name ?? user.email.split('@')[0]}
                </div>
                <div className="truncate text-[10px] text-neutral-500">{user.email}</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => logout()}
              disabled={logoutPending}
              className="flex w-full items-center justify-center gap-1.5 rounded border border-neutral-200 px-2 py-1.5 text-[11px] font-medium text-neutral-700 transition-colors hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-50"
            >
              {logoutPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <LogOut className="h-3 w-3" />
              )}
              Déconnexion
            </button>
          </>
        ) : (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-2">
            <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-rose-700">
              <AlertCircle className="h-3 w-3" />
              Non connecté
            </div>
            <div className="mb-2 text-[10px] text-rose-800">
              Les données affichées sont des mocks. Connecte-toi pour voir les vraies données.
            </div>
            <a
              href="https://cockpit.andalous-elearning.com/login"
              className="bg-primary-600 hover:bg-primary-700 inline-flex w-full items-center justify-center gap-1 rounded px-2 py-1.5 text-[10px] font-semibold text-white"
            >
              Se connecter →
            </a>
          </div>
        )}
      </div>
    </aside>
  );
}

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
  /** Si fourni, le lien est marqué actif quand l'URL courante commence par ce préfixe. */
  matchPath?: string;
}

/**
 * NavItem sous-niveau (sans icône, indenté), affiché uniquement quand le
 * parent est actif. Utilisé pour le sous-menu Paramètres.
 */
function NestedNavItem({
  to,
  label,
  parentPath,
}: {
  to: string;
  label: string;
  parentPath: string;
}) {
  const location = useLocation();
  const parentActive = location.pathname.startsWith(parentPath);
  if (!parentActive) return null;
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `ml-8 flex items-center rounded-lg px-3 py-1.5 text-xs transition-colors ${
          isActive
            ? 'bg-primary-50 text-primary-900 font-semibold'
            : 'text-neutral-600 hover:bg-neutral-50'
        }`
      }
    >
      {label}
    </NavLink>
  );
}

function NavItem({ to, icon, label, active, disabled, matchPath }: NavItemProps) {
  const location = useLocation();
  if (disabled) {
    return (
      <div className="flex cursor-not-allowed items-center gap-3 rounded-lg px-3 py-2 text-neutral-400">
        {icon}
        <span>{label}</span>
        <span className="ml-auto text-[10px] uppercase tracking-wider opacity-60">soon</span>
      </div>
    );
  }
  const matchActive = matchPath ? location.pathname.startsWith(matchPath) : false;
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 py-2 transition-colors ${
          isActive || active || matchActive
            ? 'bg-primary-50 text-primary-900 font-semibold'
            : 'text-neutral-700 hover:bg-neutral-50'
        }`
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}
